/**
 * Submissions queue. Filter chips drive the `status` query param.
 *
 * Note on filters: the backend currently only honours `status=pending`
 * (returns 400 on anything else). The Approved/Rejected chips are
 * rendered as disabled-looking placeholders with a tooltip — once the
 * backend grows those views the chips just need their disabled flag
 * removed. We kept them visible so the moderator audience knows the
 * surface is coming and doesn't ask "where do I see what I approved?".
 */
import { useCallback, useEffect, useState } from 'react';

import { ApiError, approveSubmission, listSubmissions, rejectSubmission, type AdminSubmission } from '../lib/api';
import RejectModal from '../components/RejectModal';
import SubmissionCard from '../components/SubmissionCard';

type Status = 'pending' | 'approved' | 'rejected';

export default function Submissions() {
  const [status, setStatus] = useState<Status>('pending');
  const [items, setItems] = useState<readonly AdminSubmission[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actingId, setActingId] = useState<string | null>(null);
  const [rejectTarget, setRejectTarget] = useState<AdminSubmission | null>(null);
  const [rejecting, setRejecting] = useState(false);

  const load = useCallback(async (s: Status) => {
    setItems(null);
    setError(null);
    try {
      const resp = await listSubmissions({ status: s });
      setItems(resp.submissions);
    } catch (err) {
      if (err instanceof ApiError && err.status === 400 && s !== 'pending') {
        // Backend doesn't support this filter yet — show a friendly empty state.
        setItems([]);
        setError(`The "${s}" view isn't available yet — backend support pending.`);
        return;
      }
      if (err instanceof ApiError && err.status === 403) {
        setError(
          "Access denied (403). Are you a moderator? Run `go run scripts/grant-moderator.go --email <your-email>` from the opensoi-backend repo.",
        );
        setItems([]);
        return;
      }
      setError(err instanceof Error ? err.message : 'Failed to load submissions.');
      setItems([]);
    }
  }, []);

  useEffect(() => {
    void load(status);
  }, [load, status]);

  const handleApprove = async (sub: AdminSubmission) => {
    setActingId(sub.id);
    try {
      await approveSubmission(sub.id);
      // Optimistic-ish: remove from current list. The list is currently
      // pending-only so "approved" implies "no longer in the queue".
      setItems((prev) => (prev === null ? prev : prev.filter((s) => s.id !== sub.id)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Approve failed.');
    } finally {
      setActingId(null);
    }
  };

  const handleReject = async (reason: { en: string; th: string }) => {
    if (rejectTarget === null) return;
    setRejecting(true);
    try {
      await rejectSubmission(rejectTarget.id, reason);
      setItems((prev) =>
        prev === null ? prev : prev.filter((s) => s.id !== rejectTarget.id),
      );
      setRejectTarget(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Reject failed.');
    } finally {
      setRejecting(false);
    }
  };

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold text-slate-900">Submissions queue</h1>
        <button
          type="button"
          onClick={() => void load(status)}
          className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100"
        >
          Refresh
        </button>
      </div>

      <div className="mb-4 flex flex-wrap gap-2" role="tablist" aria-label="Status filter">
        {(['pending', 'approved', 'rejected'] as const).map((s) => {
          const active = status === s;
          const disabled = s !== 'pending'; // backend hasn't shipped these yet
          return (
            <button
              key={s}
              type="button"
              role="tab"
              aria-selected={active}
              disabled={disabled}
              onClick={() => setStatus(s)}
              title={disabled ? 'Backend support pending' : undefined}
              className={[
                'rounded-full border px-3 py-1 text-sm font-medium transition',
                active
                  ? 'border-soi-500 bg-soi-50 text-soi-700'
                  : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-100',
                disabled ? 'cursor-not-allowed opacity-50' : '',
              ].join(' ')}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          );
        })}
      </div>

      {error !== null && (
        <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          {error}
        </div>
      )}

      {items === null ? (
        <div className="py-16 text-center text-sm text-slate-500">Loading submissions…</div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-white py-16 text-center">
          <p className="text-sm font-medium text-slate-700">Nothing in the queue.</p>
          <p className="mt-1 text-xs text-slate-500">
            New submissions appear here as soon as users send them in.
          </p>
        </div>
      ) : (
        <ul className="space-y-3">
          {items.map((s) => (
            <li key={s.id}>
              <SubmissionCard
                submission={s}
                busy={actingId === s.id}
                onApprove={() => void handleApprove(s)}
                onReject={() => setRejectTarget(s)}
              />
            </li>
          ))}
        </ul>
      )}

      <RejectModal
        open={rejectTarget !== null}
        busy={rejecting}
        onCancel={() => setRejectTarget(null)}
        onConfirm={(reason) => void handleReject(reason)}
      />
    </div>
  );
}
